import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardSectionHeader,
  TablePagePagination,
  dashboardDataTable as dt,
} from "src/components/common";
import RestaurantAiExecutiveSummary from "../RestaurantAiExecutiveSummary";
import RestaurantHeaderActions, { type RestaurantHeaderActionsProps } from "../RestaurantHeaderActions";
import RestaurantKpiCard from "../RestaurantKpiCard";
import RestaurantSectionCard from "../RestaurantSectionCard";
import RestaurantRevenueOrdersTrend from "../charts/RestaurantRevenueOrdersTrend";
import RestaurantHorizontalBars from "../charts/RestaurantHorizontalBars";
import RestaurantCategoryBars from "../charts/RestaurantCategoryBars";
import RestaurantAbandonedCartAnalytics from "../RestaurantAbandonedCartAnalytics";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantFilters, RestaurantMetrics } from "../../utils/restaurantMetrics";

const ORDER_TYPE_LABEL_KEYS: Record<string, string> = {
  dine_in: "restaurantAnalytics.orderTypes.dineIn",
  takeaway: "restaurantAnalytics.orderTypes.takeaway",
  delivery: "restaurantAnalytics.orderTypes.delivery",
  drive_thru: "restaurantAnalytics.orderTypes.driveThru",
  pickup: "restaurantAnalytics.orderTypes.pickup",
};

const FULFILMENT_LABEL_KEYS: Record<string, string> = {
  under_20: "restaurantAnalytics.fulfilment.under20",
  "20_30": "restaurantAnalytics.fulfilment.between20And30",
  "30_45": "restaurantAnalytics.fulfilment.between30And45",
  over_45: "restaurantAnalytics.fulfilment.over45",
};

interface RestaurantSalesOrdersTabProps {
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
  headerProps: RestaurantHeaderActionsProps;
}

const RestaurantSalesOrdersTab: FC<RestaurantSalesOrdersTabProps> = ({ metrics, filters, headerProps }) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();

  const aovByTypeBars = metrics.orderTypes.map(type => ({
    id: type.id,
    label: t(ORDER_TYPE_LABEL_KEYS[type.id] ?? type.id, { defaultValue: type.id }),
    value: type.aov,
  }));

  const fulfilmentBars = metrics.fulfilment.map(bucket => ({
    id: bucket.id,
    label: t(FULFILMENT_LABEL_KEYS[bucket.id] ?? bucket.id, { defaultValue: bucket.id }),
    value: bucket.orders,
  }));

  const dailyRowsDesc = useMemo(() => [...metrics.daily].reverse(), [metrics.daily]);

  const ROWS_PER_PAGE = 10;
  const [dailyPage, setDailyPage] = useState(1);
  const dailyTotalPages = Math.max(1, Math.ceil(dailyRowsDesc.length / ROWS_PER_PAGE));
  const paginatedDailyRows = useMemo(
    () => dailyRowsDesc.slice((dailyPage - 1) * ROWS_PER_PAGE, dailyPage * ROWS_PER_PAGE),
    [dailyRowsDesc, dailyPage],
  );
  useEffect(() => setDailyPage(1), [dailyRowsDesc]);

  return (
    <div className="restaurant-analytics-dashboard__section">
      <DashboardSectionHeader
        title={t("restaurantAnalytics.tabs.sales", { defaultValue: "Sales & Orders" })}
        subtitle={t("restaurantAnalytics.header.salesSubtitle", {
          defaultValue: "Order volume, value, fulfilment, and cancellations.",
        })}
        sticky
        actions={<RestaurantHeaderActions {...headerProps} />}
      />

      <RestaurantAiExecutiveSummary tab="sales-orders" metrics={metrics} filters={filters} />

      <div className="restaurant-analytics-dashboard__kpi-row">
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.totalRevenue", { defaultValue: "Total revenue" })}
          value={fmt.formatCurrency(metrics.revenue, { compact: true })}
          delta={metrics.revenueDelta}
          accent="primary"
          tooltip={t("restaurantAnalytics.kpiTooltips.totalRevenue", { defaultValue: "Total revenue across all orders in the selected date range and filters." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.discountsGiven", { defaultValue: "Discounts given" })}
          value={fmt.formatCurrency(metrics.discountAmount, { compact: true })}
          delta={metrics.discountAmountDelta}
          accent="warning"
          tooltip={t("restaurantAnalytics.kpiTooltips.discountsGiven", { defaultValue: "Total value of discounts applied to orders." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.refundedAmount", { defaultValue: "Refunded amount" })}
          value={fmt.formatCurrency(metrics.refundedAmount, { compact: true })}
          delta={metrics.refundedAmountDelta}
          accent="destructive"
          tooltip={t("restaurantAnalytics.kpiTooltips.refundedAmount", { defaultValue: "Total value refunded to customers in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.cancelledOrders", { defaultValue: "Cancelled orders" })}
          value={fmt.formatNumber(metrics.cancelledOrders)}
          delta={metrics.cancelledOrdersDelta}
          accent="destructive"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.cancelledOrders", { defaultValue: "Number of orders cancelled before fulfilment." })}
        />
      </div>

      <RestaurantRevenueOrdersTrend metrics={metrics} />

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantHorizontalBars
          title={t("restaurantAnalytics.charts.aovByType", { defaultValue: "Average order value by type" })}
          tip={t("restaurantAnalytics.chartTips.aovByType", { defaultValue: "Average spend per order for each order type." })}
          data={aovByTypeBars}
          valueKind="currency"
        />
        <RestaurantCategoryBars
          title={t("restaurantAnalytics.charts.fulfilmentDistribution", { defaultValue: "Fulfilment time distribution" })}
          tip={t("restaurantAnalytics.chartTips.fulfilmentDistribution", { defaultValue: "How many orders fall into each fulfilment time bracket." })}
          data={fulfilmentBars}
          valueKind="number"
          colorIndex={3}
        />
      </div>

      <RestaurantSectionCard
        title={t("restaurantAnalytics.charts.ordersVsCancellations", { defaultValue: "Orders vs cancellations" })}
        tip={t("restaurantAnalytics.chartTips.ordersVsCancellations", { defaultValue: "Daily orders, revenue, and cancellations for the selected period." })}
        bodyClassName="restaurant-table-body"
        footer={(
          <TablePagePagination
            currentPage={dailyPage}
            totalPages={dailyTotalPages}
            onPrevious={() => setDailyPage(p => Math.max(1, p - 1))}
            onNext={() => setDailyPage(p => Math.min(dailyTotalPages, p + 1))}
          />
        )}
      >
        <DashboardDataTableScroll>
          <DashboardDataTableTable className={`${dt.tableCompact} restaurant-wide-table`}>
            <thead>
              <tr>
                <th>{t("restaurantAnalytics.table.date", { defaultValue: "Date" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.orders", { defaultValue: "Orders" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.revenue", { defaultValue: "Revenue" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.aov", { defaultValue: "AOV" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.cancelled", { defaultValue: "Cancelled" })}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDailyRows.map(day => (
                <tr key={day.date}>
                  <td className={dt.cellEmphasis}>{fmt.formatDate(day.date)}</td>
                  <td className={dt.number}>{fmt.formatNumber(day.orders)}</td>
                  <td className={dt.number}>{fmt.formatCurrency(day.revenue, { compact: true })}</td>
                  <td className={dt.number}>{fmt.formatCurrencyAverage(day.aov)}</td>
                  <td className={`${dt.number} ${dt.metric} ${dt.metricDanger}`}>
                    {/* En dash, not a middot — "14 · 2.7%" reads as a decimal point. */}
                    {`${fmt.formatNumber(day.cancelled)} – ${fmt.formatPercent(day.orders > 0 ? day.cancelled / day.orders : 0, 1)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
      </RestaurantSectionCard>

      <RestaurantAbandonedCartAnalytics metrics={metrics} />
    </div>
  );
};

export default RestaurantSalesOrdersTab;
